import { useEffect, useState, useMemo, useContext } from 'react';
import {
  AgxColumn,
  AgxInlineSearch,
  AgxRow,
  AgxBodyText,
  Images,
  CampaignStage,
  useWindowSize,
  Breakpoints,
  BasicCampaignDetailModel,
  useDebouncedState,
} from '@urbanx/agx-ui-components';

import {
  fetchAllCampaigns,
  getArchivedCampaigns,
  selectCampaign,
} from 'features/campaigns/campaignsReducer';
import { LoadingState } from 'utils/loadingState';
import { AuthContext } from '@urbanx/agx.auth.client';
import { GroupedCampaigns } from './components/GroupedCampaigns/GroupedCampaigns';
import { SelectedCampaignModal } from './components/SelectedCampaignModal';
import { CampaignInfoPanelLoader } from '../CampaignInfoPanelLoader/CampaignInfoPanelLoader';
import { CampaignsViewPageImageCard } from '../CampaignsViewPageImageCard/CampaignsViewPageImageCard';
import { CampaignsTabs } from '../CampaignTabs/CampaignsTabs';
import { CampaignsListLoader } from '../CampaignsListLoader';
import CampaignInfoPanel from '../CampaignInfoPanel/CampaignInfoPanel';
import { Tabs } from 'types/Tabs';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import './CampaignsList.scss';
import { getEnumValue } from 'helpers/enumHelper';
import { useNewSelectedCampaign } from 'hooks/useNewSelectedCampaign';
import CampaignEmptyState from '../CampaignEmptyState/CampaignEmptyState';
import { AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';

const filterCampaignsWithSearchTerm = (
  campaigns: BasicCampaignDetailModel[],
  searchTerm: string | null
) => {
  if (!searchTerm) return campaigns;

  if (!campaigns) return [];

  const filterValue = searchTerm.toLowerCase();

  return campaigns.filter((campaign) => campaign.searchableAddress.includes(filterValue));
};

const agreementsStages = [
  CampaignStage.AgreementDraft,
  CampaignStage.AgreementVendorSigning,
  CampaignStage.AgreementAgentSigning,
  CampaignStage.AgreementSignedByAllParties,
  CampaignStage.AgreementSubmissionIncomplete,
  CampaignStage.AgreementPendingCompliance,
];

const listingsStages = [CampaignStage.ListingCurrent, CampaignStage.ListingUnderContract];

const getTabFilteredCampaigns = (
  campaigns: BasicCampaignDetailModel[],
  archivedCampaigns: BasicCampaignDetailModel[],
  currentTab: Tabs
): BasicCampaignDetailModel[] => {
  switch (currentTab) {
    case Tabs.All:
      return campaigns;
    case Tabs.Agreements:
      return campaigns.filter((campaign) =>
        agreementsStages.includes(getEnumValue(CampaignStage, campaign.stage))
      );
    case Tabs.Listings:
      return campaigns.filter((campaign) =>
        listingsStages.includes(getEnumValue(CampaignStage, campaign.stage))
      );
    case Tabs.Archive:
      return archivedCampaigns;
    default:
      break;
  }

  return [];
};

const tabs = [Tabs.All, Tabs.Agreements, Tabs.Listings, Tabs.Archive];

const CampaignsList = () => {
  const {
    loadingState: campaignsLoadingState,
    archivedLoadingState,
    campaigns,
    archivedCampaigns,
  } = useAppSelector((state) => state.campaigns);

  const { selectedCampaignId } = useAppSelector((state) => state.campaigns);
  const { selectedCampaign, isSelectedCampaignPending } = useNewSelectedCampaign();

  const windowSize = useWindowSize();
  const { userAccount, getAuthToken } = useContext(AuthContext);
  const [showSelectedCampaignModal, setShowSelectedCampaignModal] = useState(false);
  const [currentTab, setCurrentTab] = useState(Tabs.All);
  const [searchTerm, setSearchTerm] = useDebouncedState('', 300);

  const dispatch = useAppDispatch();

  const onCampaignClick = (campaignId: string) => {
    dispatch(selectCampaign(campaignId));

    if (windowSize === Breakpoints.Mobile) {
      setShowSelectedCampaignModal(true);
    }
  };

  useEffect(() => {
    if (!userAccount || campaignsLoadingState !== LoadingState.NotLoaded) return;

    getAuthToken().then((token) => {
      if (!token) return;
      dispatch(fetchAllCampaigns(token));
      dispatch(getArchivedCampaigns(token));
    });
  }, [userAccount, campaignsLoadingState]);

  const tabFilteredCampaigns = useMemo(() => {
    return getTabFilteredCampaigns(campaigns, archivedCampaigns, currentTab);
  }, [campaigns, archivedCampaigns, currentTab]);

  const searchFilteredCampaigns = useMemo(() => {
    return filterCampaignsWithSearchTerm(tabFilteredCampaigns, searchTerm);
  }, [tabFilteredCampaigns, searchTerm]);

  useEffect(() => {
    if (
      searchFilteredCampaigns.length > 0 &&
      !searchFilteredCampaigns.some((c) => c.campaignId === selectedCampaign?.campaignId)
    ) {
      dispatch(selectCampaign(searchFilteredCampaigns[0].campaignId));
    } else if (searchFilteredCampaigns.length === 0) {
      dispatch(selectCampaign(null));
    }
  }, [searchFilteredCampaigns, dispatch]);

  const toActionCampaigns = useMemo(() => {
    return searchFilteredCampaigns.filter((campaign) => campaign.isToAction);
  }, [searchFilteredCampaigns]);

  const allOtherCampaigns = useMemo(() => {
    return searchFilteredCampaigns.filter((campaign) => !campaign.isToAction);
  }, [searchFilteredCampaigns]);

  const showCampaignEmptyState =
    campaignsLoadingState === LoadingState.Loaded &&
    archivedLoadingState === LoadingState.Loaded &&
    campaigns.length === 0 &&
    archivedCampaigns.length === 0;

  return (
    <>
      <div className="dashboardInbox">
        <AgxRow fill spaceBetween>
          <AgxInlineSearch
            onSearchFocus={() => {}}
            onValueChanged={(value) => setSearchTerm(value)}
          />
        </AgxRow>
        <CampaignsTabs tabs={tabs} tabChanged={setCurrentTab} selectedTab={currentTab} />

        {(campaignsLoadingState === LoadingState.NotLoaded ||
          campaignsLoadingState === LoadingState.Loading) && <CampaignsListLoader />}

        {campaignsLoadingState === LoadingState.Loaded && (
          <AgxColumn mediumGap fill scrollable={!showCampaignEmptyState}>
            {!searchTerm && searchFilteredCampaigns?.length === 0 && (
              <AgxSpacer
                direction="horizontal"
                contentJustification="center"
                padding={{ top: 'spacing-6xl', bottom: 'spacing-6xl' }}
              >
                <AgxTypography
                  id="campaigns-list-empty-state"
                  type="body"
                  size="small"
                  color="var(--text-tertiary)"
                  textAlign="center"
                >
                  No campaigns found
                </AgxTypography>
              </AgxSpacer>
            )}
            {searchTerm && searchFilteredCampaigns?.length === 0 && (
              <AgxColumn fill centered>
                <Images.MenuArrowCircle />
                <AgxBodyText medium>No results for your search</AgxBodyText>
              </AgxColumn>
            )}
            {searchTerm &&
              searchFilteredCampaigns?.map((campaign, i) => (
                <CampaignsViewPageImageCard
                  key={campaign.campaignId}
                  onClick={() => onCampaignClick(campaign.campaignId)}
                  campaign={campaign}
                  index={i + 1}
                  selected={selectedCampaignId === campaign.campaignId}
                />
              ))}
            {!searchTerm && searchFilteredCampaigns?.length > 0 && (
              <>
                <GroupedCampaigns
                  title="To Action"
                  campaigns={toActionCampaigns}
                  onCampaignClick={onCampaignClick}
                />

                <GroupedCampaigns
                  title={currentTab !== Tabs.Archive ? 'All' : undefined}
                  campaigns={allOtherCampaigns}
                  onCampaignClick={onCampaignClick}
                />
              </>
            )}
          </AgxColumn>
        )}
      </div>
      {(campaignsLoadingState === LoadingState.NotLoaded ||
        campaignsLoadingState === LoadingState.Loading ||
        isSelectedCampaignPending) && <CampaignInfoPanelLoader />}

      {windowSize !== Breakpoints.Mobile &&
        !isSelectedCampaignPending &&
        showCampaignEmptyState && <CampaignEmptyState />}

      {windowSize !== Breakpoints.Mobile &&
        !isSelectedCampaignPending &&
        !showCampaignEmptyState && (
          <CampaignInfoPanel campaign={selectedCampaign} currentTab={currentTab} />
        )}
      {windowSize === Breakpoints.Mobile && showSelectedCampaignModal && (
        <SelectedCampaignModal
          selectedCampaign={selectedCampaign}
          currentTab={currentTab}
          onClosed={() => setShowSelectedCampaignModal(false)}
        />
      )}
    </>
  );
};

export default CampaignsList;
